import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import React, { useState } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Ionicons, Feather } from "@expo/vector-icons";
import { useDevice } from "../../_layout";
import { formatDayMonth } from "../../utils/dateUtils";
import Colors from "../../../Resources/Color";
import GlobalStyles from "../../../Resources/Styles";

const WorkoutsCard = observer(({ client }) => {
  const [hoveredWorkout, setHoveredWorkout] = useState(null);
  const { isMobile } = useDevice();
  const { t, i18n } = useTranslation();

  // Get active block and current week workouts
  const activeBlock = client.summaryStartDate
    ? client.activeBlock(client.summaryStartDate)
    : null;

  const hideBlockDashboard = client.coach.hideBlockDashboard ?? false;

  let workouts = [];
  let currentWeek = 0;
  let hasWorkouts = false;
  let weekWorkouts = [];

  if (activeBlock) {
    currentWeek = activeBlock.currentWeek(client.summaryStartDate);
    weekWorkouts = activeBlock.workouts
      .filter((workout) => workout.week === currentWeek)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    if (weekWorkouts && weekWorkouts.length > 0) {
      hasWorkouts = true;
      // Process workouts to extract protocol slots
      workouts = weekWorkouts.map((workout) => {
        // Get unique protocol slots from exercises
        const protocolSlots = [];
        if (workout.exercises && !hideBlockDashboard) {
          workout.exercises.forEach((exercise) => {
            if (
              exercise.protocolSlot &&
              !protocolSlots.includes(exercise.protocolSlot)
            ) {
              protocolSlots.push(exercise.protocolSlot);
            }
          });
        }

        // Format the workout date
        const workoutDate = workout.date ? new Date(workout.date) : null;
        const language = i18n.language || "en";
        const dateStr = workoutDate
          ? formatDayMonth(workoutDate, language)
          : "";

        return {
          id: workout.id,
          name: workout.name || `Day ${workout.day}`,
          date: dateStr,
          tags: protocolSlots,
          workout: workout,
        };
      });
    }
  }

  const handleWorkoutPress = (workout) => {
    // Set all workouts in the current week to isSelected = false
    weekWorkouts.forEach((w) => {
      w.setProperty("isSelected", false);
    });

    //Set selectedWeek
    activeBlock.setProperty("selectedWeek", currentWeek);
    activeBlock.setProperty("splitSelectedWeek", currentWeek);

    activeBlock.setProperty("isShowingDashboard", false);

    // Set the selected workout to isSelected = true
    workout.setProperty("isSelected", true);
    client.setSelectedBlock(activeBlock);

    client.setProperty("selectedTab", "planification-string");
  };

  return (
    <View style={GlobalStyles.Card}>
      <View style={GlobalStyles.CardHeader}>
        <View style={GlobalStyles.CardHeaderMain}>
          <View style={GlobalStyles.CardIcon}>
            <Ionicons
              name="barbell-outline"
              size={20}
              color={Colors.grayColor}
            />
          </View>
          <Text style={GlobalStyles.CardTitle}>
            {t("workoutsTitle-string")}
          </Text>
        </View>
        {hasWorkouts && (
          <View style={styles.weekBadge}>
            <Text style={styles.weekBadgeText}>
              {t("week-string")} {currentWeek}
            </Text>
          </View>
        )}
      </View>

      <View style={styles.workoutsGrid}>
        {hasWorkouts ? (
          <>
            <View style={styles.workoutList}>
              {workouts.map((workout, index, arr) => (
                <TouchableOpacity
                  key={workout.id || index}
                  style={[
                    styles.workoutRow,
                    index === arr.length - 1 && styles.workoutRowLast,
                  ]}
                  onMouseEnter={() => !isMobile && setHoveredWorkout(index)}
                  onMouseLeave={() => setHoveredWorkout(null)}
                  onPress={() => handleWorkoutPress(workout.workout)}
                >
                  <View style={styles.workoutMain}>
                    <Text style={styles.workoutName} numberOfLines={3}>
                      {workout.name}
                    </Text>
                    <Text style={styles.workoutDate}>{workout.date}</Text>
                  </View>
                  <View style={styles.workoutRightSection}>
                    <Feather
                      name="arrow-up-right"
                      size={16}
                      color={
                        hoveredWorkout === index || isMobile
                          ? Colors.grayColor
                          : "transparent"
                      }
                      style={workout.tags.length > 0 ? { marginRight: 8 } : {}}
                    />

                    <View style={styles.tagsContainer}>
                      {workout.tags.map((protocolSlot, tIndex) => {
                        const tagLabel = t(protocolSlot);
                        let color = Colors.grayColor;
                        let bg = Colors.lightGrayColor;

                        // Determine color based on protocol slot priority
                        const raw = protocolSlot.replace(/-string$/, "");
                        const idx = raw.search(/[A-Z]/);
                        const priority = raw.substring(0, idx).toLowerCase();

                        switch (priority) {
                          case "primary":
                            color = "rgb(230, 0, 0)";
                            bg = "rgba(230, 0, 0, 0.1)";
                            break;
                          case "secondary":
                            color = "rgb(63, 123, 141)";
                            bg = "rgba(63, 123, 141, 0.1)";
                            break;
                          case "tertiary":
                            color = "rgb(34, 139, 34)";
                            bg = "rgba(34, 139, 34, 0.1)";
                            break;
                          case "quaternary":
                            color = "rgb(255, 165, 0)";
                            bg = "rgba(255, 165, 0, 0.1)";
                            break;
                          case "quinary":
                            color = "rgb(255, 105, 180)";
                            bg = "rgba(255, 105, 180, 0.1)";
                            break;
                          default:
                            color = Colors.grayColor;
                            bg = Colors.lightGrayColor;
                        }

                        return (
                          <View
                            key={tIndex}
                            style={[styles.tagPill, { backgroundColor: bg }]}
                          >
                            <Text
                              style={[styles.tagText, { color: color }]}
                              numberOfLines={1}
                            >
                              {tagLabel}
                            </Text>
                          </View>
                        );
                      })}
                    </View>
                  </View>
                </TouchableOpacity>
              ))}
            </View>
          </>
        ) : (
          <View style={styles.workoutList}>
            {[1, 2, 3, 4].map((_, index) => (
              <View
                key={index}
                style={[
                  styles.workoutRow,
                  index === 3 && styles.workoutRowLast,
                  { opacity: 0.5 },
                ]}
              >
                <View style={styles.workoutMain}>
                  <View
                    style={{
                      width: 120,
                      height: 14,
                      backgroundColor: Colors.lightGrayColor,
                      borderRadius: 4,
                      marginBottom: 6,
                    }}
                  />
                  <View
                    style={{
                      width: 80,
                      height: 10,
                      backgroundColor: Colors.lightGrayColor,
                      borderRadius: 4,
                    }}
                  />
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  workoutsGrid: {
    gap: 0,
  },
  workoutList: {
    gap: 8,
  },
  workoutRow: {
    paddingVertical: 12,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderColor,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
  },
  workoutRowLast: {
    borderBottomWidth: 0,
  },
  workoutMain: {
    gap: 4,
    flex: 1,
  },
  workoutRightSection: {
    flexDirection: "row",
    alignItems: "center",
    flex: 2,
    justifyContent: "flex-end",
  },
  workoutName: {
    fontSize: 13,
    fontWeight: "600",
    color: "#000000",
  },
  workoutDate: {
    fontSize: 11,
    color: Colors.grayColor,
  },
  tagsContainer: {
    flexDirection: "row",
    gap: 6,
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    flexShrink: 1,
  },
  tagPill: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    maxWidth: "100%",
  },
  tagText: {
    fontSize: 10,
    fontWeight: "600",
  },
  weekBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: Colors.lightGrayColor,
    borderRadius: 6,
  },
  weekBadgeText: {
    fontSize: 11,
    fontWeight: "500",
    color: Colors.grayColor,
  },
});

export default WorkoutsCard;
